// Migración de hubs legacy a `/hub/tpv` (V2).
//
// Con V2 activo, los hooks legacy (useHubUsers, useUserSessionHub,
// useDeviceEnforcementHub, notificaciones, cajón, KDS, carta QR…) NO abren su
// socket: `CreateConnection` registra sus listeners en este bus y la conexión
// única `/hub/tpv` traduce cada sobre V2 al evento legacy equivalente
// (mismo nombre y mismos argumentos). Los handlers existentes no cambian.
//
// Este módulo es puro (sin sockets ni store): registro + traducción.

// Hubs cuyos eventos llegan por V2. Quedan fuera los de workers (printerworker,
// moneybox, soundhub) y `startsessionhub` (comensal QR antes de tener token).
export const BRIDGED_HUBS = [
  "userhub",
  "usersession",
  "logs",
  "workerstatus",
  "notificationrestaurantehub",
  "cashdrawer",
  "displaykitchenhub",
  "sessionhub",
  "notificationsessionhub",
  "producthub",
  "pedidohub",
  // Solo `dinerSplit/{rid}` (carta QR, reparto coordinado). El resto de mesahub
  // lo cubren los handlers V2 del TPV (TpvRealtimeHubs no abre mesahub con V2).
  "mesahub",
];

/** `{ hub, restauranteId, sessionId }` de la URL de un hub legacy, o null. */
export function parseHubUrl(url) {
  if (!url) return null;
  let parsed;
  try {
    parsed = new URL(url, "http://local");
  } catch {
    return null;
  }
  const match = parsed.pathname.match(/\/hub\/([^/?#]+)/i);
  if (!match) return null;
  return {
    hub: match[1].toLowerCase(),
    restauranteId: parsed.searchParams.get("restauranteId") || null,
    sessionId: parsed.searchParams.get("sessionId") || null,
  };
}

export function isBridgedHub(hub) {
  return !!hub && BRIDGED_HUBS.includes(String(hub).toLowerCase());
}

/**
 * Traduce un sobre V2 a los eventos legacy que lo sustituyen:
 * `[{ hub, event, args }]`. `fetchUser` (opcional) se usa para `user.updated`
 * (V2 no manda la licencia por seguridad: el legacy sí la mandaba entera);
 * en ese caso se devuelve `{ hub, event, argsFrom: "user" }` y el conector
 * resuelve el argumento pidiendo la licencia.
 */
export function mapEnvelopeToLegacy(env) {
  if (!env || !env.type) return [];
  const rid = env.restauranteId;
  const data = env.data;
  const out = (hub, event, ...args) => ({ hub, event, args });

  switch (env.type) {
    // ── userhub ────────────────────────────────────────────────────────
    case "user.updated":
      return [{ hub: "userhub", event: `user/${rid}`, args: [], argsFrom: "user" }];
    case "user.rolePermissionsChanged":
      return [out("userhub", `role-permissions-changed/${rid}`, data)];
    case "user.addOnGrantsChanged":
      return [out("userhub", `add-on-grants-changed/${rid}`, data)];
    // 2026-10 · Contador de alertas de stock (menú «Inventario y costes»).
    case "stock.alerts":
      return [out("userhub", `stock-alerts/${rid}`, data)];
    case "user.systemNotification":
      return [out("userhub", "system-notification-changed", data)];
    case "user.monitorToggle":
      return [out("userhub", `monitorToggle/${rid}`, data)];
    case "device.enforcementChanged":
      return [out("userhub", `device-enforcement/${rid}`, data)];
    case "device.forceSelection":
      return [out("userhub", `device-force-selection/${rid}`, data)];
    case "device.activeEmployeeChanged":
      return [out("userhub", `device-active-employee-changed/${rid}`, data)];
    // 2026-10-05 · Orden remota del admin (Admin › Versiones).
    case "device.command":
      return [out("userhub", `device-command/${rid}`, data)];
    case "carta.scanProgress":
      return [out("userhub", "read-carta-progress", data)];

    // ── usersession ────────────────────────────────────────────────────
    case "user.sessionsSync":
      return data?.only
        ? [out("usersession", `user/sync/only/${rid}`, data?.sessionOwner)]
        : [out("usersession", `user/sync/${rid}`)];
    case "user.sessionClosed": {
      const sid = data?.sessionIdentifier ?? (env.id !== "*" ? env.id : null);
      return sid ? [out("usersession", `user/${rid}/session/${sid}`)] : [];
    }
    case "user.removed":
      return [out("usersession", `user/${rid}`)];

    // ── logs / workerstatus ────────────────────────────────────────────
    case "printer.error":
      return [out("logs", `${rid}`, { Type: "Error", Message: data?.message ?? "Error en la impresora" })];
    case "worker.status":
      return [out("workerstatus", `workerStatus/${rid}`, data)];
    case "printer.discoveryResults":
      return [out("workerstatus", `discovery/results/${rid}`, data)];

    // ── notificationrestaurantehub ─────────────────────────────────────
    case "notification.created":
      // El aviso de sonido era para los workers (soundhub): no hay consumidor web.
      if (data && data.kind === "sound") return [];
      return [out("notificationrestaurantehub", `notification/${rid}`, data)];
    case "notification.employeeCorrections":
      return [out("notificationrestaurantehub", `employee-corrections/${rid}`, data)];
    case "notification.scheduleAlerts":
      return [out("notificationrestaurantehub", `schedule-alerts/${rid}`, data)];
    case "notification.whatsappQuota":
      return [out("notificationrestaurantehub", `whatsapp-quota-exhausted/${rid}`, data)];
    case "notification.activity":
      return [out("notificationrestaurantehub", `activity-notification/${rid}`, data)];

    // ── cashdrawer ─────────────────────────────────────────────────────
    case "cashdrawer.updated":
      return [out("cashdrawer", "cashdrawer-update", data)];
    case "cashdrawer.deviceStatus":
      return [out("cashdrawer", "cashdrawer-device-status", data)];

    // ── KDS ────────────────────────────────────────────────────────────
    case "kds.sync":
      return [out("displaykitchenhub", `displaykitchen/${rid}`)];

    // ── Catálogo (TPV sin V2 de productos y carta QR) ──────────────────
    case "products.changed": {
      const ids = Array.isArray(data?.productoIds) ? data.productoIds : [];
      const base = { idRestaurante: rid, action: data?.action ?? null, timestamp: env.ts };
      if (ids.length === 0 || data?.bulk) {
        return [out("producthub", `products/restaurante/${rid}`, { ...base, productoId: null })];
      }
      return ids.map((productoId) => out("producthub", `products/restaurante/${rid}`, { ...base, productoId }));
    }

    // ── Reparto coordinado (carta QR) ──────────────────────────────────
    case "dinerSplit.changed":
      return [out("mesahub", `dinerSplit/${rid}`, { idSession: data?.idSession ?? env.id })];

    // ── Solo sesión (comensal QR / carta del camarero) ─────────────────
    case "session.full": {
      const sid = data?.id ?? data?.ID ?? env.id;
      return sid ? [out("sessionhub", `session/${sid}`, data)] : [];
    }
    case "session.notification": {
      const sid = data?.sessionId ?? data?.payload?.idSession;
      if (!sid) return [];
      const suffix = data?.kind === "isReady" ? "/isReady" : "";
      return [out("notificationsessionhub", `notification/session/${sid}${suffix}`, data?.payload)];
    }
    case "session.pedido": {
      const sid = data?.idSession ?? data?.IdSession;
      return sid ? [out("pedidohub", `pedido/session/${sid}`, data)] : [];
    }

    default:
      return [];
  }
}

/** Registro de listeners legacy por `hub|evento`. */
export function createLegacyBus() {
  const listeners = new Map();
  const key = (hub, event) => `${String(hub).toLowerCase()}|${event}`;

  return {
    on(hub, event, fn) {
      const k = key(hub, event);
      if (!listeners.has(k)) listeners.set(k, new Set());
      listeners.get(k).add(fn);
      return () => {
        const set = listeners.get(k);
        if (!set) return;
        set.delete(fn);
        if (set.size === 0) listeners.delete(k);
      };
    },
    has(hub, event) {
      return listeners.has(key(hub, event));
    },
    emit(hub, event, args = []) {
      const set = listeners.get(key(hub, event));
      if (!set) return 0;
      let n = 0;
      set.forEach((fn) => {
        try {
          fn(...args);
          n += 1;
        } catch (err) {
          // Un handler roto no debe tumbar al resto.
          // eslint-disable-next-line no-console
          console.warn(`[realtime-bridge] handler ${hub}/${event} falló`, err);
        }
      });
      return n;
    },
    size() {
      let n = 0;
      listeners.forEach((s) => { n += s.size; });
      return n;
    },
  };
}
